import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  TextField,
  Button,
  Autocomplete,
  Paper,
  InputAdornment,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { MovieCard } from '../components/MovieCard';
import { tmdbService } from '../services';
import { Movie } from '../types';
import Navbar from '../components/Navbar';

const ITEMS_PER_PAGE = 20;

const MovieCardSkeleton = () => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.1)',
    }}
  >
    <Skeleton
      variant="rectangular"
      width="100%"
      sx={{ paddingTop: '150%', bgcolor: 'rgba(255,255,255,0.1)' }}
    />
    <CardContent sx={{ flexGrow: 1, p: 2 }}>
      <Skeleton variant="text" width="80%" height={32} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Skeleton variant="text" width={60} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      </Box>
    </CardContent>
  </Card>
);

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [allMovies, setAllMovies] = useState<Movie[]>([]);

  const { data: trendingData, isLoading: isLoadingTrending } = useQuery(
    ['trending', page],
    () => tmdbService.getTrendingMovies(page),
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    }
  );

  const { data: searchData, isLoading: isLoadingSearch } = useQuery(
    ['search', searchQuery, page],
    () => tmdbService.searchMovies(searchQuery, page),
    {
      enabled: searchQuery.length > 0,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    }
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      if (value.length > 2) {
        setSearchQuery(value);
        setPage(1);
      } else {
        setSearchQuery('');
      }
    },
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, debouncedSearch]);

  useEffect(() => {
    if (searchQuery) {
      if (searchData?.results) {
        setAllMovies(prev => page === 1 ? searchData.results : [...prev, ...searchData.results]);
      }
    } else if (trendingData?.results) {
      setAllMovies(prev => page === 1 ? trendingData.results : [...prev, ...trendingData.results]);
    }
  }, [searchData, trendingData, page, searchQuery]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const handleMovieClick = (movie: Movie) => {
    navigate(`/movie/${movie.id}`);
  };

  const isLoading = searchQuery ? isLoadingSearch : isLoadingTrending;
  const totalPages = searchQuery ? searchData?.total_pages : trendingData?.total_pages;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', color: 'white' }}>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: { xs: 8, md: 10 } }}>
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          px: { xs: 2, md: 4 }
        }}>
          <Typography 
            variant="h2" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '2rem', md: '3.5rem' },
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Stream Your Favorite Movies in HD Quality
          </Typography>
          
          {/* Disclaimer Text */}
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              fontSize: { xs: '0.9rem', md: '1rem' },
              maxWidth: '800px',
              margin: '0 auto',
              fontStyle: 'italic',
              lineHeight: 1.6
            }}
          >
            This platform serves as an informational hub for movie enthusiasts, providing comprehensive reviews, 
            ratings, and streaming availability across various OTT platforms. We do not host or distribute any 
            copyrighted content. All content is sourced from publicly available information and official streaming 
            platforms. Users are encouraged to access content through legitimate streaming services and respect 
            intellectual property rights.
          </Typography>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 6 }}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px'
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search movies..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  '&::placeholder': {
                    color: 'rgba(255,255,255,0.5)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Box>

        {/* Movie Grid */}
        <Grid container spacing={3}>
          {(isLoadingTrending || isLoadingSearch) ? (
            // Loading skeletons
            Array.from(new Array(ITEMS_PER_PAGE)).map((_, index) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={index}>
                <MovieCardSkeleton />
              </Grid>
            ))
          ) : (
            // Movie cards
            (searchQuery ? searchData?.results : trendingData?.results)?.map((movie) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={movie.id}>
                <div onClick={() => handleMovieClick(movie)}>
                  <MovieCard {...movie} />
                </div>
              </Grid>
            ))
          )}
        </Grid>

        {/* Load More Button */}
        {!isLoadingTrending && !isLoadingSearch && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                px: 4,
                py: 1.5
              }}
            >
              Load More
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}; 